import { gql } from '@apollo/client';
import { Bool, Num, Obj, Str } from '@livecontrol/core-utils';
import type {SchedulingDefaults } from '@livecontrol/scheduler/model';
import type { PartialDeep } from 'type-fest';

export const QUERY = gql`
query GetSchedulingDefaultsByClientId($clientId: Int!) {
  getSchedulingDefaultsByClientId(client_id: $clientId) {
    id
    location {
      id
    }
    default_slides {
      id
      slide_type
      file_name
      file_type
      url
      duration
      size
      position_index
      created_at
      updated_at
    }
    settings {
      password_protect {
        enabled
        favored
      }
      password {
        enabled
        favored
      }
      live_chat {
        enabled
        favored
      }
      event_registration {
        enabled
        favored
      }
      require_registration {
        enabled
        favored
      }
      guestbook {
        enabled
        favored
      }
      slides {
        enabled
        favored
      }
      show_viewers {
        enabled
        favored
      }
      join_zoom_event {
        enabled
        favored
      }
      iso_recordings {
        enabled
        favored
      }
      cue_sheet {
        enabled
        favored
      }
      redirect_viewers {
        enabled
        favored
      }
      video_notes {
        enabled
        favored
      }
      destinations {
        enabled
        favored
      }
      recurring {
        enabled
        favored
      }
      on_site_contact {
        enabled
        favored
      }
      custom_background {
        enabled
        favored
      }
    }
  }
}
`;


export interface SchedulingDefaultsSetting<T = boolean> {
  favored: boolean | undefined;
  enabled: T | undefined;
}

export type Record = PartialDeep<{
    id: number;
    location: {
      id: string;
    };
    default_slides: {
      id: number;
      slide_type: string;
      file_name: string;
      file_type: string;
      url: string;
      duration: number;
      size: number;
      position_index: number;
      created_at: string;
      updated_at: string;
    }[];
    settings: {
      recurring: SchedulingDefaultsSetting;
      on_site_contact: SchedulingDefaultsSetting;
      custom_background: SchedulingDefaultsSetting;
      password_protect: SchedulingDefaultsSetting;
      password: SchedulingDefaultsSetting<string>;
      live_chat: SchedulingDefaultsSetting;
      event_registration: SchedulingDefaultsSetting;
      require_registration: SchedulingDefaultsSetting;
      guestbook: SchedulingDefaultsSetting;
      slides: SchedulingDefaultsSetting;
      show_viewers: SchedulingDefaultsSetting;
      join_zoom_event: SchedulingDefaultsSetting;
      iso_recordings: SchedulingDefaultsSetting;
      cue_sheet: SchedulingDefaultsSetting<string>;
      redirect_viewers: SchedulingDefaultsSetting<string>;
      video_notes: SchedulingDefaultsSetting<string>;
      destinations: SchedulingDefaultsSetting<string[]> | undefined;
    };
}>;

export const normalize = (record?: Record): SchedulingDefaults | undefined => {
    const id = Num.normalize(record?.id);

    let location: Partial<SchedulingDefaults['location']>;

    if (record?.location) {
        location = {
            id: Str.normalize(record.location.id)
        };
    }

    const default_slides = record?.default_slides?.map(s=> ({
        id: Num.normalize(s?.id) ?? 0,
        slide_type: Str.normalize(s?.slide_type) ?? '',
        file_name: Str.normalize(s?.file_name) ?? '',
        file_type: Str.normalize(s?.file_type) ?? '',
        url: Str.normalize(s?.url) ?? '',
        duration: Num.normalize(s?.duration) ?? 0,
        size: Num.normalize(s?.size) ?? 0,
        position_index:Num.normalize(s?.position_index) ?? 0,
        created_at: Str.normalize(s?.created_at) ?? '',
        updated_at: Str.normalize(s?.updated_at) ?? ''
      })) ?? [];

    const settings = {
        customBackground: false, // This field is not present in the record, defaulting to false
        recurring: false,
        onSiteContact: false,
        password: Str.normalize(record?.settings?.password?.enabled) ?? '',
        liveChat: Bool.normalize(record?.settings?.live_chat?.enabled),
        eventRegistration: Bool.normalize(record?.settings?.event_registration?.enabled),
        requireRegistration: Bool.normalize(record?.settings?.require_registration?.enabled),
        guestbook: Bool.normalize(record?.settings?.guestbook?.enabled),
        slides: Bool.normalize(record?.settings?.slides?.enabled),
        showViewers: Bool.normalize(record?.settings?.show_viewers?.enabled),
        joinZoomEvent: Bool.normalize(record?.settings?.join_zoom_event?.enabled),
        iso: Bool.normalize(record?.settings?.iso_recordings?.enabled),
        cueSheetUrl: Str.normalize(typeof record?.settings?.cue_sheet?.enabled === 'string' ? record.settings.cue_sheet.enabled : undefined) ?? '',
        redirectUrl: Str.normalize(record?.settings?.redirect_viewers?.enabled) ?? '',
        videoNotes: Str.normalize(record?.settings?.video_notes?.enabled) ?? '',
        destinations: Array.isArray(record?.settings?.destinations?.enabled) ? record?.settings?.destinations?.enabled.filter((v): v is string => typeof v === 'string') ?? [] : []
    };

    const settingsFavored = {
      customBackground: Bool.normalize(record?.settings?.custom_background?.favored),
      recurring: Bool.normalize(record?.settings?.recurring?.favored),
      onSiteContact: Bool.normalize(record?.settings?.on_site_contact?.favored),
      password: Bool.normalize(record?.settings?.password?.favored),
      liveChat: Bool.normalize(record?.settings?.live_chat?.favored),
      eventRegistration: Bool.normalize(record?.settings?.event_registration?.favored),
      requireRegistration: Bool.normalize(record?.settings?.require_registration?.favored),
      guestbook: Bool.normalize(record?.settings?.guestbook?.favored),
      slides: Bool.normalize(record?.settings?.slides?.favored),
      showViewers: Bool.normalize(record?.settings?.show_viewers?.favored),
      joinZoomEvent: Bool.normalize(record?.settings?.join_zoom_event?.favored),
      iso: Bool.normalize(record?.settings?.iso_recordings?.favored),
      cueSheetUrl: Bool.normalize(record?.settings?.cue_sheet?.favored),
      redirectUrl: Bool.normalize(record?.settings?.redirect_viewers?.favored),
      videoNotes: Bool.normalize(record?.settings?.video_notes?.favored),
      destinations: Bool.normalize(record?.settings?.destinations?.favored)
    };

    if (!id) {
        return undefined;
    }

    return { id, location: Obj.isHydrated(location) ? location : undefined, settings, default_slides, settingsFavored };
};
